import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { invalidateHubAvatar } from "@/lib/hub/useHubAvatarUrl";
import { normalizeHubDisplayName, type HubAvatarStatus } from "@/lib/hub/hubIdentity";
import { HUB_IDENTITIES_KEY } from "@/hooks/hub/useHubIdentities";

/**
 * LR HUB settings hook — reads/writes ONLY `public.hub_settings`.
 * Never touches profiles / companies / billing.
 *
 * Identity model: `display_name` (spaces allowed, not unique) + `handle`
 * (unique, lowercase, rate-limited to one change every 30 days).
 */

export interface HubSettings {
  user_id: string;
  display_name: string;
  handle: string;
  show_role: boolean;
  avatar_status: HubAvatarStatus;
  avatar_path: string | null;
  handle_changed_at: string | null;
  guidelines_accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

export const hubSettingsKey = (userId?: string | null) => ["hub-settings", userId ?? "anon"];

/**
 * Translates a Postgres error into a clean, member-facing message.
 * The only real uniqueness guarantee is the DB unique index on
 * `lower(handle)` (`hub_settings_handle_lower_uniq`) — when two members
 * race for the same handle, the loser lands here.
 */
function mapHubError(err: unknown): Error {
  const e = err as { code?: string; message?: string } | null;
  const msg = e?.message ?? "";
  if (e?.code === "23505" || /hub_settings_handle_lower_uniq|duplicate key/i.test(msg)) {
    return new Error("That handle was just taken. Please pick another.");
  }
  return err instanceof Error ? err : new Error("Something went wrong. Please try again.");
}


export function useHubSettings() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: hubSettingsKey(userId),
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<HubSettings | null> => {
      const { data, error } = await supabase
        .from("hub_settings")
        .select("*")
        .eq("user_id", userId!)
        .maybeSingle();
      if (error) throw error;
      return (data as HubSettings | null) ?? null;
    },
  });
}

/** Live uniqueness check for the @handle. */
export function useHubHandleCheck() {
  return useMutation({
    mutationFn: async (handle: string): Promise<boolean> => {
      const { data, error } = await supabase.rpc("hub_handle_available", {
        _handle: handle.trim().toLowerCase(),
      });
      if (error) throw error;
      return !!data;
    },
  });
}

/** Creates the member's Hub identity row (first-time onboarding). */
export function useCreateHubSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { displayName: string; handle: string; showRole: boolean }) => {
      if (!user?.id) throw new Error("You must be signed in.");
      const { data, error } = await supabase
        .from("hub_settings")
        .insert({
          user_id: user.id,
          display_name: normalizeHubDisplayName(input.displayName),
          handle: input.handle.trim().toLowerCase(),
          show_role: input.showRole,
          guidelines_accepted_at: new Date().toISOString(),
        })
        .select("*")
        .single();
      if (error) throw mapHubError(error);

      return data as HubSettings;
    },
    onSuccess: (row) => {
      qc.setQueryData(hubSettingsKey(user?.id), row);
      qc.invalidateQueries({ queryKey: hubSettingsKey(user?.id) });
      qc.invalidateQueries({ queryKey: HUB_IDENTITIES_KEY });
    },
  });
}

export function useUpdateHubSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      patch: Partial<{ display_name: string; handle: string; show_role: boolean }>,
    ) => {
      if (!user?.id) throw new Error("You must be signed in.");
      const payload: Record<string, unknown> = {};
      if (patch.display_name !== undefined) {
        payload.display_name = normalizeHubDisplayName(patch.display_name);
      }
      if (patch.handle !== undefined) payload.handle = patch.handle.trim().toLowerCase();
      if (patch.show_role !== undefined) payload.show_role = patch.show_role;
      const { data, error } = await supabase
        .from("hub_settings")
        .update(payload)
        .eq("user_id", user.id)
        .select("*")
        .single();
      if (error) throw mapHubError(error);

      return data as HubSettings;
    },
    onSuccess: (row) => {
      qc.setQueryData(hubSettingsKey(user?.id), row);
      // The feed reads display name / @handle / role-visibility / avatar LIVE
      // through `hub_display_identities` — drop those cached rows so the
      // change shows on existing posts and comments right away.
      qc.invalidateQueries({ queryKey: HUB_IDENTITIES_KEY });
    },
  });
}

/**
 * Uploads a photo into the member's own folder in the private `hub-avatars`
 * bucket and flags it `pending`. The member keeps showing initials until a
 * LaneRunner moderator approves it.
 */
export function useUploadHubAvatar() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) throw new Error("You must be signed in.");
      if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
        throw new Error("Use a PNG, JPG or WEBP image.");
      }
      if (file.size > 4 * 1024 * 1024) {
        throw new Error("Image must be smaller than 4 MB.");
      }
      const ext = file.type.includes("png") ? "png" : file.type.includes("webp") ? "webp" : "jpg";
      const path = `${user.id}/hub-avatar-${Date.now()}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from("hub-avatars")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;

      const { data, error } = await supabase
        .from("hub_settings")
        .update({ avatar_path: path, avatar_status: "pending" })
        .eq("user_id", user.id)
        .select("*")
        .single();
      if (error) throw error;
      invalidateHubAvatar(path);
      return data as HubSettings;
    },
    onSuccess: (row) => {
      qc.setQueryData(hubSettingsKey(user?.id), row);
      // The feed reads display name / @handle / role-visibility / avatar LIVE
      // through `hub_display_identities` — drop those cached rows so the
      // change shows on existing posts and comments right away.
      qc.invalidateQueries({ queryKey: HUB_IDENTITIES_KEY });
    },
  });
}

/** Member reverts to the generated initials avatar. */
export function useRemoveHubAvatar() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("You must be signed in.");
      const { data, error } = await supabase
        .from("hub_settings")
        .update({ avatar_path: null, avatar_status: "initials" })
        .eq("user_id", user.id)
        .select("*")
        .single();
      if (error) throw error;
      return data as HubSettings;
    },
    onSuccess: (row) => {
      qc.setQueryData(hubSettingsKey(user?.id), row);
      // The feed reads display name / @handle / role-visibility / avatar LIVE
      // through `hub_display_identities` — drop those cached rows so the
      // change shows on existing posts and comments right away.
      qc.invalidateQueries({ queryKey: HUB_IDENTITIES_KEY });
    },
  });
}
